import type { Entries } from './catalog';

/**
 * The copy of an AI app's permissions (2026-10-31): who receives access, what it already has and what
 * it asks for now, grouped by what each permission does, what the decision leaves it able to do, the
 * confirmation of a removal, an access that changed while the page was open, and narrowing it from AI
 * apps with access. The permissions' own words are each product's; nothing here names one product.
 *
 * Each area keeps its own file so its keys travel with its screens; a key here must not repeat one of
 * `en.ts`, `es.ts` or another area.
 */
export const permissions: Record<'en' | 'es', Entries> = {
	en: {
		'permissions.route': 'Who receives access',
		'permissions.route.answers': 'Answers at {host}',
		'permissions.route.local': 'On this computer, at {host}',
		'permissions.route.for': 'For {person}',

		'permissions.held': 'Already allowed',
		'permissions.held.lead': 'It keeps all of this unless you remove something.',
		'permissions.held.change': 'Remove some',
		'permissions.held.keep': 'Keep all',
		'permissions.held.removing': 'Uncheck what it should no longer do. It must ask again to get it back.',
		'permissions.asked': 'Asked for now',
		'permissions.asked.lead': 'Reading is chosen for you. Choose anything that changes your work, reaches outside Beyond or can cost money.',
		'permissions.asked.choose': 'Choose what it may do.',
		'permissions.asked.none': 'It asks for nothing it does not already have.',
		'permissions.count': '{chosen} of {total} chosen',

		'permissions.group.read': 'See',
		'permissions.group.change': 'Change',
		'permissions.group.publish': 'Reach outside Beyond',
		'permissions.group.cost': 'Spend',
		'permissions.group.other': 'Other',
		'permissions.effect.read': 'Read only',
		'permissions.effect.change': 'Makes changes',
		'permissions.effect.publish': 'Outside Beyond',
		'permissions.effect.cost': 'Can cost money',
		'permissions.mark.add': 'Will be added',
		'permissions.mark.remove': 'Will be removed',
		'permissions.undescribed': '{product} no longer describes this permission',
		'permissions.english': 'Only in English',
		'permissions.name': 'Permission: {name}',

		'permissions.overlap.all': 'It also has access in all your organizations: {list}. That access stays as it is.',
		'permissions.overlap.some': 'It also has separate access in {list}. That access stays as it is.',
		'permissions.overlap.left': 'an organization you no longer belong to',

		'permissions.summary': 'After you allow it',
		'permissions.adds': 'Adds',
		'permissions.keeps': 'Keeps',
		'permissions.removes': 'Removes',
		'permissions.nothing': 'Choose at least one permission to allow it.',
		'permissions.where': 'Where',
		'permissions.until': 'Until',
		'permissions.removal': 'What you remove ends at once in Accounts and within 30 seconds in {product}.',
		'permissions.cancel': 'Cancel keeps what it already has.',

		'permissions.confirm.one': 'Remove “{permission}” from {client}?',
		'permissions.confirm.many': 'Remove {count} permissions from {client}?',
		'permissions.confirm.body': 'It can no longer: {list}.',
		'permissions.confirm.affected': 'Work it already started keeps running.',
		'permissions.confirm.lost': 'Its access that could do it ends at once in Accounts and within 30 seconds in {product}.',
		'permissions.confirm.recovery': 'It can ask again, and you choose again.',
		'permissions.confirm.allow': 'Allow and remove',
		'permissions.confirm.remove': 'Remove permissions',

		'permissions.changed': 'Its access changed while this page was open',
		'permissions.changed.body': 'Nothing was changed. Review what it has now.',

		'connections.connection': 'Its connection',
		'connections.permissions.change': 'Change permissions',
		'connections.permissions.hint': 'Uncheck what it should no longer do. To add a permission, connect again from {client}.',
		'connections.permissions.remove': 'Remove permissions',
		'connections.permissions.removed': '{client} can no longer: {list}.'
	},
	es: {
		'permissions.route': 'Quién recibe acceso',
		'permissions.route.answers': 'Responde en {host}',
		'permissions.route.local': 'En este equipo, en {host}',
		'permissions.route.for': 'Para {person}',

		'permissions.held': 'Ya permitido',
		'permissions.held.lead': 'Conserva todo esto, salvo lo que quites.',
		'permissions.held.change': 'Quitar algo',
		'permissions.held.keep': 'Conservar todo',
		'permissions.held.removing': 'Desmarca lo que ya no debe hacer. Tendrá que pedirlo de nuevo para recuperarlo.',
		'permissions.asked': 'Lo que pide ahora',
		'permissions.asked.lead': 'Leer viene elegido. Elige lo que cambia tu trabajo, llega fuera de Beyond o puede costar dinero.',
		'permissions.asked.choose': 'Elige qué puede hacer.',
		'permissions.asked.none': 'No pide nada que no tenga ya.',
		'permissions.count': '{chosen} de {total} elegidos',

		'permissions.group.read': 'Ver',
		'permissions.group.change': 'Cambiar',
		'permissions.group.publish': 'Llegar fuera de Beyond',
		'permissions.group.cost': 'Gastar',
		'permissions.group.other': 'Otros',
		'permissions.effect.read': 'Solo lectura',
		'permissions.effect.change': 'Hace cambios',
		'permissions.effect.publish': 'Fuera de Beyond',
		'permissions.effect.cost': 'Puede costar dinero',
		'permissions.mark.add': 'Se añadirá',
		'permissions.mark.remove': 'Se quitará',
		'permissions.undescribed': '{product} ya no describe este permiso',
		'permissions.english': 'Solo en inglés',
		'permissions.name': 'Permiso: {name}',

		'permissions.overlap.all': 'También tiene acceso en todas tus organizaciones: {list}. Ese acceso sigue igual.',
		'permissions.overlap.some': 'También tiene acceso aparte en {list}. Ese acceso sigue igual.',
		'permissions.overlap.left': 'una organización a la que ya no perteneces',

		'permissions.summary': 'Al permitirlo',
		'permissions.adds': 'Añade',
		'permissions.keeps': 'Conserva',
		'permissions.removes': 'Quita',
		'permissions.nothing': 'Elige al menos un permiso para permitirlo.',
		'permissions.where': 'Dónde',
		'permissions.until': 'Hasta',
		'permissions.removal': 'Lo que quites termina al instante en Accounts y en 30 segundos como mucho en {product}.',
		'permissions.cancel': 'Cancelar conserva lo que ya tiene.',

		'permissions.confirm.one': '¿Quitar «{permission}» a {client}?',
		'permissions.confirm.many': '¿Quitar {count} permisos a {client}?',
		'permissions.confirm.body': 'Ya no podrá: {list}.',
		'permissions.confirm.affected': 'El trabajo que ya empezó sigue en marcha.',
		'permissions.confirm.lost': 'Su acceso que podía hacerlo termina al instante en Accounts y en 30 segundos como mucho en {product}.',
		'permissions.confirm.recovery': 'Puede pedirlo de nuevo, y tú eliges otra vez.',
		'permissions.confirm.allow': 'Permitir y quitar',
		'permissions.confirm.remove': 'Quitar permisos',

		'permissions.changed': 'Su acceso cambió mientras esta página estaba abierta',
		'permissions.changed.body': 'No se cambió nada. Revisa lo que tiene ahora.',

		'connections.connection': 'Su conexión',
		'connections.permissions.change': 'Cambiar permisos',
		'connections.permissions.hint': 'Desmarca lo que ya no debe hacer. Para añadir un permiso, conéctala de nuevo desde {client}.',
		'connections.permissions.remove': 'Quitar permisos',
		'connections.permissions.removed': '{client} ya no puede: {list}.'
	}
};
